// Company endpoints. listCompanies returns the companies the logged-in user
// can open — called after login, before a company is selected, so it uses
// the loginToken. The backend nests each row as { company: {...} }; we flatten
// it to the { id, name } the UI needs.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { Company } from '@/mocks/companies';
import type { AuthRole, AuthPermission, AuthUser, CompanyDetail } from '@/redux/auth/authSlice';

interface CompanyItem {
  companyCode: string;
  companyId: string;
  companyName: string;
  nameOnMidani: string | null;
}

/** Raw `users/company-invites` row. Richer than CompanyItem: alongside the
 *  company it carries the placement, roles, inviter and date the invite detail
 *  screen shows. */
interface ApiCompanyInvite {
  companyCode: string;
  companyId: string;
  companyName: string;
  nameOnMidani: string | null;
  roles: { name: string }[];
  nodes: { name: string }[];
  addedBy: { firstName: string; lastName: string };
  createdAt: string;
}

/** A pending invitation the logged-in user can accept or ignore, flattened for
 *  the UI (keeping the code for display). */
export interface CompanyInvite {
  id: string;
  name: string;
  code: string;
  nameOnMidani: string | null;
  /** Role names granted by this invite. */
  roles: string[];
  /** Structure nodes the invitee is being placed under. */
  nodes: string[];
  /** Full name of whoever sent the invite. */
  invitedBy: string;
  /** ISO datetime the invite was created. */
  sentAt: string;
}

/** Company-scoped session returned by select-company: the tokens plus the
 *  user's access data, which the app stores and gates on (no JWT decode). */
export interface SelectCompanyResult {
  token: string;
  refreshToken: string;
  companyId: string;
  company: CompanyDetail;
  roles: AuthRole[];
  permissions: AuthPermission[];
  user: AuthUser;
  /** The current user's own company-user record id (confirmed always present). */
  companyUserId: string;
}

/** Owner of the current company — fetched right after select-company;
 *  `companyUserId` is the owner's company-user record id. */
export interface OwnerDetailsResult {
  companyUserId: string;
}

/** Employee-count bands sent as `employeeRange` when creating a company. */
export enum CompanyEmployeeRange {
  FROM_1_TO_10 = 'from_1_to_10',
  FROM_10_TO_100 = 'from_10_to_100',
  FROM_100_TO_1000 = 'from_100_to_1000',
  ABOVE_1000 = 'above_1000',
}

/** Body for create-company. `dashboardFeatures` is a JSON-stringified id array;
 *  `mediaId` is the uploaded-logo reference (omitted when no logo was chosen). */
export interface CreateCompanyPayload {
  companyName: string;
  title: string;
  businessEmail: string;
  employeeRange: CompanyEmployeeRange;
  businessIndustry: string;
  dashboardFeatures: string;
  mediaId?: string;
}

/** One requested profile field on the invite form; `required` gates the invitee. */
export interface RequiredField {
  fieldName: string;
  required: boolean;
}

/** Invite-by-link configuration (POST body === GET's non-null `inviteLinkDetails`). */
export interface InviteLinkSettings {
  notifyMeOnJoinCompany: boolean;
  requiredFields: RequiredField[];
  groups: string[];
  companyNodes: string[];
  roles: string[];
}

export interface InviteLinkDetailsResponse {
  inviteLinkDetails: InviteLinkSettings | null;
  inviteCode: string;
}

export const companyApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listCompanies: build.query<Company[], void>({
      query: () => ({ ...ROUTES.companies.list }),
      transformResponse: (res: CompanyItem[]) =>
        (res ?? []).map((c) => ({ id: c.companyId, name: c.companyName })),
      providesTags: ['Company'],
    }),
    // Exchanges the loginToken for a company-scoped session. Body carries the
    // chosen company id (field name per spec — assumed `companyId`).
    selectCompany: build.mutation<SelectCompanyResult, { companyId: string }>({
      query: (body) => ({ ...ROUTES.companies.selectCompany, data: body }),
    }),
    // The current company's owner. Called imperatively (lazy) by the switch flow,
    // which copies the value into the auth slice and then resets this cache.
    getOwnerDetails: build.query<OwnerDetailsResult, void>({
      query: () => ({ ...ROUTES.companies.ownerDetails }),
    }),
    // Pending invitations shown on /my-invites, ahead of company selection.
    getCompanyInvites: build.query<CompanyInvite[], void>({
      query: () => ({ ...ROUTES.companies.invites }),
      transformResponse: (res: ApiCompanyInvite[]) =>
        res.map((c) => ({
          id: c.companyId,
          name: c.companyName,
          code: c.companyCode,
          nameOnMidani: c.nameOnMidani,
          roles: c.roles.map((r) => r.name),
          nodes: c.nodes.map((n) => n.name),
          invitedBy: `${c.addedBy.firstName} ${c.addedBy.lastName}`.trim(),
          sentAt: c.createdAt,
        })),
      providesTags: ['CompanyInvite'],
    }),
    // Accept invites → the companies become selectable (refetch both lists).
    acceptInvite: build.mutation<unknown, { companies: string[] }>({
      query: (body) => ({ ...ROUTES.companies.acceptInvite, data: body }),
      invalidatesTags: ['Company', 'CompanyInvite'],
    }),
    // Onboards a new company for the logged-in user (loginToken). Returns the new
    // company's id (fed straight into select-company). Invalidates the company list.
    createCompany: build.mutation<{ companyId: string }, CreateCompanyPayload>({
      query: (body) => ({ ...ROUTES.companies.create, data: body }),
      invalidatesTags: ['Company'],
    }),
    // Invite-by-link settings for the current company (Add people → Invite tab).
    getInviteLinkDetails: build.query<InviteLinkDetailsResponse, void>({
      query: () => ({ ...ROUTES.companies.inviteLinkDetails }),
      providesTags: ['Invite'],
    }),
    saveInviteLinkDetails: build.mutation<unknown, InviteLinkSettings>({
      query: (body) => ({ ...ROUTES.companies.saveInviteLinkDetails, data: body }),
      invalidatesTags: ['Invite'],
    }),
    // Sends the invite link to the caller's own mobile. No body, and no
    // invalidation either: it dispatches a message and mutates nothing cached.
    sendInviteLinkToMobile: build.mutation<unknown, void>({
      query: () => ({ ...ROUTES.companies.sendLinkToMobile }),
    }),
  }),
});

export const {
  useListCompaniesQuery,
  useSelectCompanyMutation,
  useLazyGetOwnerDetailsQuery,
  useCreateCompanyMutation,
  useGetCompanyInvitesQuery,
  useAcceptInviteMutation,
  useGetInviteLinkDetailsQuery,
  useSaveInviteLinkDetailsMutation,
  useSendInviteLinkToMobileMutation,
} = companyApi;
